import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface CoverImageUploadProps {
  userId: string;
  currentImage: string;
  onImageUploaded: (url: string) => void;
  className?: string;
}

export const CoverImageUpload: React.FC<CoverImageUploadProps> = ({
  userId,
  currentImage,
  onImageUploaded,
  className
}) => {
  const [preview, setPreview] = useState<string>(currentImage);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const validateFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      toast({
        title: "Formato non supportato",
        description: "Carica un file JPG, PNG o WEBP",
        variant: "error",
      });
      return false;
    }

    if (file.size > maxSize) {
      toast({
        title: "File troppo grande",
        description: "La dimensione massima è 10MB",
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const cropToAspectRatio = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Target dimensions: 1200x400 (3:1 ratio)
        const TARGET_WIDTH = 1200;
        const TARGET_HEIGHT = 400;
        const TARGET_RATIO = 3 / 1;

        const sourceRatio = img.width / img.height;

        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let sourceX = 0;
        let sourceY = 0;

        // Crop to 3:1 ratio
        if (sourceRatio > TARGET_RATIO) {
          // Image is wider than 3:1, crop sides
          sourceWidth = img.height * TARGET_RATIO;
          sourceX = (img.width - sourceWidth) / 2;
        } else if (sourceRatio < TARGET_RATIO) {
          // Image is taller than 3:1, crop top/bottom
          sourceHeight = img.width / TARGET_RATIO;
          sourceY = (img.height - sourceHeight) / 2;
        }

        // Set canvas size to target dimensions
        canvas.width = TARGET_WIDTH;
        canvas.height = TARGET_HEIGHT;

        // Draw cropped image to canvas
        ctx?.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight,
          0, 0, TARGET_WIDTH, TARGET_HEIGHT
        );

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/jpeg',
          0.9 // Quality 90%
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const deleteOldImage = async (imageUrl: string) => {
    try {
      const urlParts = imageUrl.split('/');
      const pathIndex = urlParts.findIndex(part => part === 'cover-images');
      if (pathIndex === -1) return;

      const filePath = urlParts.slice(pathIndex + 1).join('/');
      
      await supabase.storage
        .from('cover-images')
        .remove([filePath]);
    } catch (error) {
      console.error('Error deleting old image:', error);
    }
  };

  const uploadImage = async (file: File) => {
    if (!validateFile(file)) return;

    try {
      setIsUploading(true);

      // Crop image to 1200x400px (3:1 ratio)
      const croppedBlob = await cropToAspectRatio(file);

      // Generate local preview
      const previewUrl = URL.createObjectURL(croppedBlob);
      setPreview(previewUrl);

      // Delete old image if exists
      if (currentImage) {
        await deleteOldImage(currentImage);
      }

      // Generate unique filename
      const timestamp = Date.now();
      const filePath = `${userId}/cover-${timestamp}.jpg`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('cover-images')
        .upload(filePath, croppedBlob, {
          contentType: 'image/jpeg',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('cover-images')
        .getPublicUrl(filePath);

      // Notify parent component
      onImageUploaded(publicUrl);

      toast({
        title: "Immagine caricata!",
        description: "La tua immagine di copertina è stata aggiornata (1200x400px)",
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare l'immagine. Riprova.",
        variant: "error",
      });
      setPreview(currentImage); // Revert to current image
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleRemove = async () => {
    if (currentImage) {
      await deleteOldImage(currentImage);
      setPreview('');
      onImageUploaded('');
      toast({
        title: "Immagine rimossa",
        description: "L'immagine di copertina è stata rimossa",
      });
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Preview current image */}
      {preview && (
        <div className="relative w-full h-[200px] border-[3px] border-dark shadow-[4px_4px_0px_#101322]">
          <img
            src={preview}
            alt="Cover preview"
            className="w-full h-full object-cover"
          />
          <button
            onClick={handleRemove}
            disabled={isUploading}
            className="absolute top-2 right-2 p-2 bg-destructive border-2 border-dark text-white hover:bg-destructive/90 disabled:opacity-50"
            type="button"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Drag & drop upload area */}
      <div
        onClick={() => !isUploading && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          "border-[3px] border-dashed border-dark p-8 text-center cursor-pointer transition-colors",
          isDragging && "bg-primary/10 border-primary",
          isUploading && "opacity-50 cursor-not-allowed"
        )}
      >
        {isUploading ? (
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
        ) : (
          <>
            <Upload className="w-12 h-12 mx-auto mb-4 text-dark" />
            <p className="font-heading font-bold mb-2">
              Trascina un'immagine qui o clicca per caricare
            </p>
            <p className="text-sm text-muted-foreground">
              JPG, PNG o WEBP (max 10MB)
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              L'immagine verrà automaticamente ridimensionata a 1200x400px (ratio 3:1)
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isUploading}
        />
      </div>
    </div>
  );
};
